import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CircularProgress } from '@mui/material';
import { Spinner as DsSpinner, cn } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { toDsProps } from './toDsProps';

import type { CircularProgressProps } from '@mui/material';

export type SpinnerProps = CircularProgressProps & {
  /** Typed because hyphenated JSX attributes are unchecked; `toDsProps` has to see it. */
  'data-testid'?: string;
};

/**
 * The legacy `CircularProgress`, for a spinner that stands on its own. A load that gates a whole route
 * or page is a different component, and so is a control's own `loading`.
 */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>((props, ref) => {
  const { t } = useTranslation('common', { keyPrefix: 'spinner' });

  // The visual-refresh gate lives here, not at the call sites. `null` falls through to MUI.
  const dsProps = VISUAL_REFRESH ? toDsProps(props) : null;

  if (!dsProps) return <CircularProgress ref={ref} {...props} />;

  const { size, inheritColor, className, ariaLabel, testId } = dsProps;

  return (
    <DsSpinner
      ref={ref}
      size={size}
      label={ariaLabel ?? t('label')}
      className={cn(inheritColor && 'text-inherit', className)}
      data-testid={testId}
    />
  );
});
